import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { stagger } from '../utils/format';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { keywordThemes, quoted, type ReviewTheme } from '../utils/reviews';
import { topReviews, type Review } from '../utils/site';

const chip1Def: SlotGroupDefinition = {
  label: 'Theme 1',
  description: 'Until generated or edited, the six chips show the themes most reviews mention, each with a quote from the newest of them.',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "The theme this community's four- and five-star reviews raise most often, as a one-to-three-word label, with a short verbatim fragment (two to eight words) from one review as the quote. A theme raised by a single review does not earn a chip.",
    context: 'Chip 1 of 6 in What residents mention on the Reviews page. The quote carries the tone on its own; never invent wording.',
  },
};

const chip2Def: SlotGroupDefinition = {
  label: 'Theme 2',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "The next theme several of this community's four- and five-star reviews raise, as a one-to-three-word label, with a verbatim two-to-eight-word fragment from one of them as the quote.",
    context: 'Chip 2 of 6 in What residents mention on the Reviews page. Do not repeat chip 1. Leave empty when no further theme is raised by more than one review.',
  },
};

const chip3Def: SlotGroupDefinition = {
  label: 'Theme 3',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "A third theme more than one of this community's four- and five-star reviews raise, labelled in one to three words, with a verbatim two-to-eight-word fragment from a review as the quote.",
    context: 'Chip 3 of 6 in What residents mention on the Reviews page. Do not repeat chips 1 and 2. Leave empty when no further theme is raised by more than one review.',
  },
};

const chip4Def: SlotGroupDefinition = {
  label: 'Theme 4',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "A fourth theme at least two of this community's four- and five-star reviews raise, labelled in one to three words, with a verbatim two-to-eight-word fragment from a review as the quote.",
    context: 'Chip 4 of 6 in What residents mention on the Reviews page. Do not repeat chips 1 to 3. Leave empty when no further theme is raised by more than one review.',
  },
};

const chip5Def: SlotGroupDefinition = {
  label: 'Theme 5',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "Another theme at least two of this community's four- and five-star reviews raise, labelled in one to three words, with a verbatim two-to-eight-word fragment from a review as the quote.",
    context: 'Chip 5 of 6 in What residents mention on the Reviews page. Optional; do not repeat chips 1 to 4. Leave empty when no further theme is raised by more than one review.',
  },
};

const chip6Def: SlotGroupDefinition = {
  label: 'Theme 6',
  fields: {
    label: { label: 'Theme', type: 'text', defaultValue: '' },
    signal: { label: 'Quote', type: 'text', defaultValue: '' },
  },
  generator: {
    prompt:
      "One last theme at least two of this community's four- and five-star reviews raise, labelled in one to three words, with a verbatim two-to-eight-word fragment from a review as the quote.",
    context: 'Chip 6 of 6 in What residents mention on the Reviews page. Optional; do not repeat chips 1 to 5. Leave empty when no further theme is raised by more than one review.',
  },
};

/** Four to six themes from the reviews, each a label over a quote; hides below four. */
export function ReviewThemes() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const slots = [
    useSlotGroup('reviewthemes.chip_1', chip1Def),
    useSlotGroup('reviewthemes.chip_2', chip2Def),
    useSlotGroup('reviewthemes.chip_3', chip3Def),
    useSlotGroup('reviewthemes.chip_4', chip4Def),
    useSlotGroup('reviewthemes.chip_5', chip5Def),
    useSlotGroup('reviewthemes.chip_6', chip6Def),
  ];

  // Once any chip is generated or edited the six slots are the whole set, so a left-empty chip stays empty.
  const custom = slots.some((s) => s.label);
  const fallback = custom ? [] : keywordThemes(topReviews(p.reviews as Review[]));
  const chips: Array<ReviewTheme | null> = slots.map((s, i) => (custom ? (s.label ? { label: s.label, signal: s.signal || '' } : null) : fallback[i] || null));
  const shown = chips.filter(Boolean).length;

  if (shown < 4 && !editing) return null;

  const chip = (i: number) => {
    const c = chips[i];
    if (!c)
      return editing ? (
        <div className="border-t border-dashed border-[--q-border] pb-5 pt-[18px] text-sm text-muted-foreground">Theme {i + 1} (empty)</div>
      ) : null;
    return (
      <div
        className="flex flex-col gap-2 border-t border-[--q-border] pb-5 pt-[18px] max-[1199px]:pb-[18px] max-[1199px]:pt-4 max-[767px]:gap-1.5 max-[767px]:pb-4 max-[767px]:pt-3.5"
        data-reveal
        style={stagger(i)}
      >
        <div className="font-heading text-[22px] leading-7 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{c.label}</div>
        {quoted(c.signal) && <div className="text-sm leading-5 text-muted-foreground">{quoted(c.signal)}</div>}
      </div>
    );
  };

  return (
    <section
      className="flex items-start gap-20 bg-[--q-paper] px-[--gutter] pb-20 pt-[88px] max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-8 max-[1199px]:pb-16 max-[1199px]:pt-[72px] max-[767px]:gap-5 max-[767px]:py-12"
      id="review-themes"
    >
      <div className="flex w-[360px] shrink-0 flex-col gap-4 max-[1199px]:w-auto max-[1199px]:max-w-[640px] max-[1199px]:gap-3.5 max-[767px]:gap-3" data-reveal>
        <RichTextContent path="reviewthemes.eyebrow" as="div" className="eyebrow max-[767px]:text-[11px] max-[767px]:leading-[14px]" />
        <RichTextContent
          path="reviewthemes.title"
          as="h2"
          className="h2 [&.h2]:text-[48px] [&.h2]:leading-[54px] max-[1199px]:[&.h2]:text-[40px] max-[1199px]:[&.h2]:leading-[46px] max-[767px]:[&.h2]:text-[34px] max-[767px]:[&.h2]:leading-[38px]"
        />
        <RichTextContent
          path="reviewthemes.lead"
          as="p"
          className="m-0 hidden text-base leading-[26px] text-muted-foreground max-[1199px]:[&:not(:empty)]:block max-[767px]:text-[15px] max-[767px]:leading-[23px]"
        />
      </div>
      <div className="grid min-w-0 flex-1 grid-cols-[repeat(2,minmax(0,1fr))] gap-x-6 [&>div:empty]:hidden max-[767px]:grid-cols-[minmax(0,1fr)]">
        <EditableSlotGroup groupPath="reviewthemes.chip_1" definition={chip1Def}>
          {chip(0)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviewthemes.chip_2" definition={chip2Def}>
          {chip(1)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviewthemes.chip_3" definition={chip3Def}>
          {chip(2)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviewthemes.chip_4" definition={chip4Def}>
          {chip(3)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviewthemes.chip_5" definition={chip5Def}>
          {chip(4)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviewthemes.chip_6" definition={chip6Def}>
          {chip(5)}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewThemes',
  displayName: 'Review Themes',
  tokens: {
    'reviewthemes.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>What residents mention</p>' },
    'reviewthemes.title': { label: 'Title', type: 'richtext', defaultValue: '<p>What comes up in the reviews.</p>' },
    'reviewthemes.lead': {
      label: 'Lead (tablet and phone)',
      type: 'richtext',
      defaultValue: "<p>Pulled from the reviews below, in the reviewers' own words.</p>",
    },
  },
  slotGroups: {
    'reviewthemes.chip_1': chip1Def,
    'reviewthemes.chip_2': chip2Def,
    'reviewthemes.chip_3': chip3Def,
    'reviewthemes.chip_4': chip4Def,
    'reviewthemes.chip_5': chip5Def,
    'reviewthemes.chip_6': chip6Def,
  },
};

(ReviewThemes as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewThemes;
